import styled, { css } from 'styled-components';

import { Box } from '@/components/Box';
import { Button } from '@/components/Button';
import { Flex } from '@/components/Flex';
import { Price as BasePrice } from '@/components/Price';
import { TaxedPrice } from '@/components/TaxedPrice';
import { Typography } from '@/components/Typography';
import { conditionalStyle, px2rem, responsive } from '@/theme';

import { ImagePlaceholder } from '../ImagePlaceholder';

import { ReactComponent as ApplePayWhite } from '@/assets/icons/applePayWhite.svg';

export const SummaryWrapper = styled(Box)<{ $showPadding?: boolean }>`
  ${({ $showPadding, theme }) =>
    $showPadding &&
    css`
      padding: ${theme.spacing(2)};

      ${theme.media.md.up} {
        padding: ${theme.spacing(4)};
      }
    `}
`;

export const LabelValueWrapper = styled(Flex)<{ isBold?: boolean }>`
  justify-content: space-between;

  ${({ isBold, theme }) => css`
    margin-bottom: ${theme.spacing(2)};

    ${conditionalStyle(isBold, 'font-weight', theme.fontWeights.bold)}
  `}
`;

export const ProductWrapper = styled(Flex)`
  justify-content: space-between;
`;

export const Label = styled(Typography)`
  max-width: 50%;
  text-transform: uppercase;

  ${({ theme }) => css`
    color: ${theme.colors.secondary};
    font-family: ${theme.fontFamilies.italianPlate};
    font-size: ${theme.fontSizes.p2};
    ${theme.media.md.up} {
      font-size: ${theme.fontSizes.h5};
    }
  `}
`;

export const Value = styled(BasePrice)`
  ${({ theme }) => css`
    & > p {
      color: ${theme.colors.primaryDark};
      font-family: ${theme.fontFamilies.italianPlate};
    }
  `}
`;

export const DeliveryFeeFreeValue = styled(Box)`
  text-transform: uppercase;

  ${({ theme }) => css`
    p {
      color: ${theme.colors.primaryDark};
      font-family: ${theme.fontFamilies.italianPlate};
    }
  `}
`;

export const TotalWrapper = styled(Box)<{ withBottomBorder?: boolean }>`
  ${({ theme, withBottomBorder }) => css`
    border-top: 1px solid ${theme.colors.mediumGrey};
    padding-block: ${theme.spacing(2)};

    ${withBottomBorder &&
    css`
      border-bottom: 1px solid ${theme.colors.mediumGrey};
      margin-bottom: ${theme.spacing(2)};
    `}
  `}
`;

export const TotalLabel = styled(Typography)`
  text-transform: uppercase;

  ${({ theme }) => css`
    color: ${theme.colors.secondary};
    font-family: ${theme.fontFamilies.italianPlate};
    font-size: ${theme.fontSizes.h4};
    font-weight: ${theme.fontWeights.bold};
  `}
`;

export const TotalValue = styled(BasePrice)`
  ${({ theme }) => css`
    & > p,
    span {
      color: ${theme.colors.primaryDark};
      font-family: ${theme.fontFamilies.italianPlate};
      font-size: ${theme.fontSizes.h4};
      font-weight: ${theme.fontWeights.bold};
    }
  `}
`;

export const SalesTaxCalculatedAtCheckout = styled(Typography)`
  text-align: right;

  ${({ theme }) => css`
    color: ${theme.colors.secondaryGrey};
    font-size: ${theme.fontSizes.p3};
  `}
`;

export const Description = styled(Typography)`
  line-height: 1.25;

  ${({ theme }) => css`
    font-size: ${theme.fontSizes.p3};
    margin-bottom: ${theme.spacing(2)};
  `}
`;

export const ShippingAndTaxesNote = styled(Typography)`
  line-height: 1.5;

  ${({ theme }) => css`
    color: ${theme.colors.primaryBlue};
    font-size: ${theme.fontSizes.p2};
    padding: 12px;
    background-color: ${theme.colors.tertiaryGrey};
    margin-bottom: ${theme.spacing(2)};
  `}
`;

export const SalesTaxInfo = styled(Typography)`
  text-align: right;
  ${({ theme }) => css`
    color: ${theme.colors.secondaryGrey};
    line-height: ${theme.lineHeights.p1};
    font-size: ${theme.fontSizes.p3};
  `}
`;

export const CheckoutButton = styled(Button)`
  width: 100%;

  ${({ theme }) => css`
    margin-bottom: ${theme.spacing(2)};
  `}
`;

export const ApplePayButton = styled.button`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 50px;

  ${({ theme }) => css`
    background-color: ${theme.colors.black};
    margin-bottom: ${theme.spacing(2)};
    font-weight: ${theme.fontWeights.bold};
  `}
`;

export const ApplePayText = styled(Typography)`
  text-transform: uppercase;

  ${({ theme }) => css`
    color: ${theme.colors.white};
  `}
`;

export const ApplePayIcon = styled(ApplePayWhite)`
  width: 54px;
  height: 28px;
`;

export const PayFinanceButton = styled(Button)`
  width: 100%;

  ${({ theme }) => css`
    background-color: ${theme.colors.white};
    border: 2px solid ${theme.colors.black};
    margin-bottom: ${theme.spacing(3)};

    &:hover {
      color: ${theme.colors.primary};
    }
  `}
`;

export const AllTransactionAreSafeAndSecure = styled(Typography)`
  text-align: center;
  text-transform: uppercase;
  font-weight: bold;

  ${({ theme }) => css`
    margin-bottom: ${theme.spacing(1)};
  `}
`;

export const PaymentLogos = styled(Flex)`
  justify-content: center;
  align-items: center;
  gap: ${px2rem(10)};
  flex-wrap: wrap;

  ${({ theme }) => css`
    margin-bottom: ${theme.spacing(1)};

    svg {
      width: 56px;
      height: 33px;
    }
  `}
`;

export const EncryptedPayment = styled(Typography)`
  text-align: center;
  line-height: 1.25;

  ${({ theme }) => css`
    font-size: ${theme.fontSizes.p3};
  `}
`;

export const Disclaimer = styled(Typography)`
  text-align: center;
  line-height: 1.25;

  ${({ theme }) => css`
    font-size: ${theme.fontSizes.p3};
    margin-bottom: ${theme.spacing(2)};
  `}
`;

export const Title = styled(Typography)`
  &&& {
    margin: 0;
    text-transform: uppercase;
    text-align: center;
    padding-bottom: 16px;
    ${({ theme }) => css`
      font-weight: ${theme.fontWeights.bold};
      font-size: ${theme.fontSizes.h4};
      font-family: ${theme.fontFamilies.italianPlate};
    `}
  }
`;

export const Wrapper = styled(Box)<{
  isBuckleChild: boolean;
  isChild: boolean;
}>`
  &&&& {
    margin: 0;
    padding-top: 0;
    padding-bottom: 0;
    ${({ theme }) => css`
      padding-inline: ${theme.spacing(4)};
    `}
    ${({ isBuckleChild, isChild, theme }) => css`
      ${isChild &&
      css`
        padding-block: ${theme.spacing(0)};
        padding-inline: ${theme.spacing(0)};
      `}
      ${isBuckleChild &&
      css`
        ${responsive('margin', px2rem(8, 0, 0, 64), px2rem(8, 0, 0, 64))};
        background: ${theme.colors.lightGrey};
        ${responsive('padding', px2rem(0, 8), px2rem(0, 8))};
      `}
    `}
  }
`;

export const BorderWrapper = styled(Box)<{
  isBuckleChild: boolean;
  isChild: boolean;
}>`
  &&& {
    ${({ isBuckleChild, isChild, theme }) => css`
      padding-block: ${isChild ? theme.spacing(1) : theme.spacing(2)};
      border-bottom: 1px solid ${theme.colors.primaryAdd6}
        ${isChild && `border-bottom: 0`} ${isBuckleChild && `border-bottom: 0`};
    `}
  }
`;

export const TextWrapper = styled(Box)`
  word-break: break-word;
  ${({ theme }) => css`
    margin-inline: ${theme.spacing(2)};
  `}
`;

export const Name = styled(Typography)<{ $productCanPurchase?: boolean }>`
  text-transform: uppercase;

  ${({ $productCanPurchase = true, theme }) => css`
    font-family: ${theme.fontFamilies.italianPlate};
    font-size: ${theme.fontSizes.p1};
    color: ${$productCanPurchase
      ? theme.colors.secondary
      : theme.colors.secondaryGrey};
  `}
`;

export const SmallText = styled(Typography)<{
  $productCanPurchase?: boolean;
}>`
  ${({ $productCanPurchase = true, theme }) => css`
    font-size: ${theme.fontSizes.p3};
    color: ${$productCanPurchase
      ? theme.colors.secondaryGrey
      : theme.colors.secondaryGreyAlpha48};
  `}
`;

export const Price = styled(TaxedPrice)<{ $productCanPurchase?: boolean }>`
  ${({ $productCanPurchase = true, theme }) => css`
    & > p {
      font-family: ${theme.fontFamilies.italianPlate};
      font-size: ${theme.fontSizes.h5};
      color: ${$productCanPurchase
        ? theme.colors.primaryDark
        : theme.colors.secondaryGrey};
    }
  `}
`;

export const ProductImageWrapper = styled.div`
  position: relative;
  display: inline-block;
`;

export const ProductImageOverlay = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  color: white;
  z-index: 1;
  text-align: center;
  font-style: normal;
  line-height: 120%;
  text-transform: uppercase;
  ${({ theme }) => css`
    font-family: ${theme.fontFamilies.openSans};
    font-size: ${theme.fontSizes.p4};
    color: ${theme.colors.white};
    background-color: ${theme.colors.defaultAlpha48};
    font-weight: ${theme.fontWeights.bold};
  `}
`;

export const PlaceHolderImage = styled(ImagePlaceholder)`
  height: 64px;
  width: 64px;
  min-width: 64px;
  max-width: 64px;
  min-height: 64px;
  max-height: 64px;
`;

export const GiftWrapper = styled(Flex)`
  flex-direction: column;
  align-items: flex-start;
  margin-bottom: ${({ theme }) => theme.spacing(2)};
`;
